import ExternalRegisterPage from './ExternalRegisterPage';

const COLUMNS = [
  ['EMA Number', 'ema_product_number'],
  ['Medicine Name', 'name_of_medicine'],
  ['INN', 'international_non_proprietary_name'],
  ['Active Substance', 'active_substance'],
  ['ATC Code', 'atc_code'],
  ['Therapeutic Area', 'therapeutic_area_mesh'],
  ['Medicine Status', 'medicine_status'],
  ['MA Holder', 'marketing_authorisation_developer_applicant_holder'],
  ['MA Date', 'european_commission_decision_date'],
  ['Opinion Date', 'opinion_adopted_date'],
  ['Generic', 'generic'],
  ['Biosimilar', 'biosimilar'],
  ['Orphan', 'orphan_medicine'],
  ['Conditional Approval', 'conditional_approval'],
  ['Medicine URL', 'medicine_url'],
];

const DATE_COLS = new Set(['european_commission_decision_date', 'opinion_adopted_date', 'marketing_authorisation_date']);

const FILTER_CONFIGS = [
  { key: 'emaSearch', label: 'EMA Number', type: 'search', column: 'ema_product_number', placeholder: 'Search by EMA product number...' },
  { key: 'inn', label: 'INN', column: 'international_non_proprietary_name' },
  { key: 'medName', label: 'Medicine Name', column: 'name_of_medicine' },
  { key: 'maHolder', label: 'MA Holder', column: 'marketing_authorisation_holder' },
  { key: 'maDate', label: 'MA Date', column: 'european_commission_decision_date' },
  // Gear-hidden
  { key: 'activeSub', label: 'Active Substance', column: 'active_substance' },
  { key: 'atcCode', label: 'ATC Code (Human)', column: 'atc_code' },
  { key: 'atcVet', label: 'ATCvet Code', column: 'atcvet_code' },
  { key: 'therArea', label: 'Therapeutic Area', column: 'therapeutic_area_mesh' },
  { key: 'therIndication', label: 'Therapeutic Indication', column: 'therapeutic_indication' },
  { key: 'pharmaGroupH', label: 'Pharmacotherapeutic Group (Human)', column: 'pharmacotherapeutic_group_human' },
  { key: 'pharmaGroupV', label: 'Pharmacotherapeutic Group (Vet)', column: 'pharmacotherapeutic_group_veterinary' },
  { key: 'species', label: 'Species', column: 'species' },
  { key: 'medStatus', label: 'Medicine Status', column: 'medicine_status' },
  { key: 'authStatus', label: 'Authorisation Status', column: 'authorisation_status' },
  { key: 'opStatus', label: 'Opinion Status', column: 'opinion_status' },
  { key: 'latestProc', label: 'Latest Procedure', column: 'latest_procedure_affecting_product_information' },
  { key: 'generic', label: 'Generic', column: 'generic' },
  { key: 'biosimilar', label: 'Biosimilar', column: 'biosimilar' },
  { key: 'condApproval', label: 'Conditional Approval', column: 'conditional_approval' },
  { key: 'exceptCirc', label: 'Exceptional Circumstances', column: 'exceptional_circumstances' },
  { key: 'accelAssess', label: 'Accelerated Assessment', column: 'accelerated_assessment' },
  { key: 'orphan', label: 'Orphan Medicine', column: 'orphan_medicine' },
  { key: 'advTherapy', label: 'Advanced Therapy', column: 'advanced_therapy' },
  { key: 'addMon', label: 'Additional Monitoring', column: 'additional_monitoring' },
  { key: 'prime', label: 'PRIME', column: 'prime_priority_medicine' },
  { key: 'patSafety', label: 'Patient Safety', column: 'patient_safety' },
  { key: 'firstPub', label: 'First Published Date', column: 'first_published_date' },
  { key: 'lastUpd', label: 'Last Updated Date', column: 'last_updated_date' },
  { key: 'opAdoptDate', label: 'Opinion Adopted Date', column: 'opinion_adopted_date' },
  { key: 'evalDate', label: 'Start of Evaluation Date', column: 'start_of_evaluation_date' },
  { key: 'rollDate', label: 'Start of Rolling Review Date', column: 'start_of_rolling_review_date' },
  { key: 'withdrawDate', label: 'Withdrawal of Application Date', column: 'withdrawal_of_application_date' },
  { key: 'refusalDate', label: 'Refusal Date', column: 'refusal_of_marketing_authorisation_date' },
  { key: 'werlDate', label: 'Withdrawal/Expiry/Revocation/Lapse Date', column: 'withdrawal_expiry_revocation_lapse_date' },
  { key: 'suspDate', label: 'Suspension Date', column: 'suspension_date' },
];

const DEFAULT_FILTERS = ['emaSearch', 'inn', 'medName', 'maHolder', 'maDate'];

const DETAIL_SECTIONS = [
  {
    title: 'General Information',
    fields: [
      { key: 'ema_product_number', label: 'EMA Product Number' },
      { key: 'name_of_medicine', label: 'Name of Medicine' },
      { key: 'international_non_proprietary_name', label: 'INN' },
      { key: 'active_substance', label: 'Active Substance' },
      { key: 'atc_code', label: 'ATC Code (Human)' },
    ],
  },
  {
    title: 'Status',
    fields: [
      { key: 'medicine_status', label: 'Medicine Status' },
      { key: 'opinion_status', label: 'Opinion Status' },
      { key: 'authorisation_status', label: 'Authorisation Status' },
      { key: 'latest_procedure_affecting_product_information', label: 'Latest Procedure', wide: true },
    ],
  },
  {
    title: 'Therapeutic',
    fields: [
      { key: 'therapeutic_area_mesh', label: 'Therapeutic Area (MeSH)' },
      { key: 'pharmacotherapeutic_group_human', label: 'Pharmacotherapeutic Group (Human)' },
      { key: 'pharmacotherapeutic_group_veterinary', label: 'Pharmacotherapeutic Group (Vet)' },
      { key: 'species', label: 'Species (Veterinary)' },
      { key: 'therapeutic_indication', label: 'Therapeutic Indication', wide: true },
    ],
  },
  {
    title: 'Authorisation',
    fields: [
      { key: 'marketing_authorisation_developer_applicant_holder', label: 'MA Holder', wide: true },
      { key: 'european_commission_decision_date', label: 'EC Decision Date' },
      { key: 'opinion_adopted_date', label: 'Opinion Adopted Date' },
      { key: 'start_of_evaluation_date', label: 'Start of Evaluation Date' },
      { key: 'start_of_rolling_review_date', label: 'Start of Rolling Review Date' },
      { key: 'withdrawal_of_application_date', label: 'Withdrawal of Application Date' },
      { key: 'refusal_of_marketing_authorisation_date', label: 'Refusal Date' },
      { key: 'withdrawal_expiry_revocation_lapse_date', label: 'Withdrawal/Expiry/Revocation/Lapse Date' },
      { key: 'suspension_date', label: 'Suspension Date' },
    ],
  },
  {
    title: 'Flags',
    fields: [
      { key: 'advanced_therapy', label: 'Advanced Therapy' },
      { key: 'prime_priority_medicine', label: 'PRIME' },
      { key: 'generic', label: 'Generic' },
      { key: 'biosimilar', label: 'Biosimilar' },
      { key: 'conditional_approval', label: 'Conditional Approval' },
      { key: 'exceptional_circumstances', label: 'Exceptional Circumstances' },
      { key: 'accelerated_assessment', label: 'Accelerated Assessment' },
      { key: 'orphan_medicine', label: 'Orphan Medicine' },
      { key: 'additional_monitoring', label: 'Additional Monitoring' },
      { key: 'patient_safety', label: 'Patient Safety' },
    ],
  },
  {
    title: 'Reference',
    fields: [
      { key: 'first_published_date', label: 'First Published Date' },
      { key: 'last_updated_date', label: 'Last Updated Date' },
      { key: 'medicine_url', label: 'Medicine URL', wide: true },
    ],
  },
];

export default function EmaSupps() {
  return (
    <ExternalRegisterPage
      source="ema"
      title="EMA Register"
      breadcrumb="EMA"
      sourceUrl="https://www.ema.europa.eu/en/medicines"
      columns={COLUMNS}
      dateColumns={DATE_COLS}
      defaultFilterKeys={DEFAULT_FILTERS}
      filterConfigs={FILTER_CONFIGS}
      prefsKey="emaFilters"
      detailTitle="EMA Register Record Details"
      detailSections={DETAIL_SECTIONS}
    />
  );
}
